import DashboardLayout from "@/PageComponents/Dashboard/DashboardLayout";
import { FC, useContext, useEffect, useLayoutEffect } from "react";
import { useSetAtom } from "jotai";
import { justCompletedSetupAtom, onboardingDashboardShownAtom } from "@/utils/atoms";
import { Box } from "@mantine/core";
import SubscriptionContext from "@/utils/subscription-context";
import Storage from "@/utils/storage";
import * as Enums from "@/utils/enums";
import OnboardingDashboard, { OnboardingSkeleton } from "@/PageComponents/Onboarding/OnboardingDashboard";
import { useOnboardingStatus } from "@/PageComponents/Onboarding/useOnboardingStatus";
import { getGraduationReason, isOnboardingEligible } from "@/PageComponents/Onboarding/onboarding-rules";
import { ONBOARDING_EVENTS, trackOnboardingOnce } from "@/PageComponents/Onboarding/onboarding-analytics";
import onboardingStyles from "@/PageComponents/Onboarding/OnboardingDashboard.module.css";

const DashboardPageComponent: FC<{testing: boolean}> = ({ testing = false}: any) => {

    const subscriptionContext = useContext<any>(SubscriptionContext);
    const subscriptionInfo = subscriptionContext?.subscriptionInfo || Storage.getCookie(Enums.Cookie.subscriptionInfo);
    const eligible = isOnboardingEligible(subscriptionInfo);
    const onboardingStatus = useOnboardingStatus(eligible);

    const setJustCompletedSetup = useSetAtom(justCompletedSetupAtom);
    const loading = eligible && onboardingStatus.isLoading;
    const graduationReason = getGraduationReason(subscriptionInfo, {
        allComplete: onboardingStatus.steps.allComplete,
        demoHeld: onboardingStatus.demo.held,
        jobCount: onboardingStatus.jobCount,
    });
    const showOnboarding = eligible && graduationReason === null;

    // The switch to the regular dashboard, and which rule caused it: once per company in this browser,
    // and kept on the person the first time. It's seen on the first dashboard visit after the rule is met.
    useEffect(() => {
        if (!eligible || onboardingStatus.isLoading || !graduationReason) return;
        trackOnboardingOnce('graduated', ONBOARDING_EVENTS.graduated, { reason: graduationReason, page: 'dashboard' }, {
            onboarding_graduated_reason: graduationReason,
            onboarding_graduated_at: new Date().toISOString(),
        });
    }, [eligible, onboardingStatus.isLoading, graduationReason]);

    // Holds back Layout's "Mobile View Not Supported" nag while onboarding is on screen. A layout
    // effect, so the flag is set before the first paint and the nag never flashes up.
    const setOnboardingDashboardShown = useSetAtom(onboardingDashboardShownAtom);
    const onboardingOnScreen = loading || showOnboarding;
    useLayoutEffect(() => {
        setOnboardingDashboardShown(onboardingOnScreen);
    }, [onboardingOnScreen]);
    useLayoutEffect(() => () => setOnboardingDashboardShown(false), []);

    // Only the onboarding view consumes this flag; don't let it linger (it suppresses the
    // navbar's enforced password-change prompt).
    useEffect(() => {
        if (!loading && !showOnboarding) setJustCompletedSetup(false);
    }, [loading, showOnboarding]);

    if (loading) {
        return <Box className={onboardingStyles.shell}><OnboardingSkeleton /></Box>;
    }

    if (showOnboarding) {
        return <Box className={onboardingStyles.shell}><OnboardingDashboard status={onboardingStatus} /></Box>;
    }

    return (
        <>
            <Box
                bg={'#F5F5F5'}
            >
                <DashboardLayout testing={testing}/>
            </Box>
        </>);
};

export default DashboardPageComponent;
